import { Box, Collapse, Grid } from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import { useAtom } from 'jotai';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import LoadingIndicator from '@/components/LoadingIndicator';

import AcronisTopics from '../AcronisTopics';
import useCategoryValidations from '../CloudSourceHooks/useCategoryValidations.hooks';
import { useSourceDetailsForEdit } from '../CloudSourceHooks/useCloudSourceEditDetails.hooks';
import useCloudSourceForm from '../CloudSourceHooks/useCloudSourceForm.hooks';
import useCustomValidatons from '../CloudSourceHooks/useCustomValidations.hooks';
import useSourceFormState from '../CloudSourceHooks/useSourceFormState.hooks';
import DataProvider from '../DataProvider/DataProvider';
import FetchDeviceVulnerability from '../DeviceVulnerability';
import FetchCustomObjects from '../FetchCustomObjects';
import {
  AuthModeSelector,
  CertificateControls,
  ClientSecretFields,
  ExistingCertificateFields,
  NewCertificateFields
} from '../renderers/AuthenticationComponents';
import ActiveToggle from '../renderers/components/ActiveToggle';
import AlertDeviceFieldsSelector from '../renderers/components/AlertDeviceFieldsSelector';
import CategorySelector from '../renderers/components/CategorySelector';
import DynamicFieldsList from '../renderers/components/DynamicFieldsList';
import IngestDataTabs from '../renderers/components/IngestDataTabs';
import MultiLevelCategorySelector from '../renderers/components/MultiLevelCategorySelector';
import { OrgDeviceIPFields } from '../renderers/components/OrgDeviceIPFields';
import RegionSelector from '../renderers/components/RegionSelector';
import { SourceTypeSelector } from '../renderers/components/SourceTypeSelector';
import SiteNameField from '../SiteNameField/SiteNameField';
import SOURCE_TYPE, { WIZARD_STEP } from '../SourceCreate.constants';
import {
  wizardCurrentStepAtom,
  wizardSelectedSourceAtom
} from '../SourceCreate.store';
import useSourceCreateStyles from '../SourceCreate.styles';
import { type DynamicFieldData, SourceDialogMode } from '../SourceCreate.types';
import SourceInfoBlock from '../SourceInfoBlock/SourceInfoBlock';
import SubSourceTypeSelector from '../SubSourceTypeSelector';
import SubTimeInterval from '../SubTimeInterval';
import useFormStyles from './SourceCreateForm.styles';

/**
 * Helper predicates for conditional rendering.
 * These extract complex conditions to improve readability and testability.
 */

/** Checks if site name field should render before SubSourceTypeSelector */
const shouldRenderSiteNameFirst = (sourceTypeId: number): boolean =>
  sourceTypeId === SOURCE_TYPE.MICROSOFT_ENTRA_ID;

/** Checks if the ingest config tab is active */
const isIngestConfigTabActive = (selectedTab: string): boolean =>
  selectedTab === 'ingest_method_config';

/** Checks if the data provider tab is active */
const isDataProviderTabActive = (selectedTab: string): boolean =>
  selectedTab === 'data_provider';

/** Checks if time interval fields should be shown (Juniper Mist specific) */
const shouldShowTimeInterval = (
  sourceTypeId: number,
  timeIntervalInputs: DynamicFieldData[] | undefined
): boolean =>
  sourceTypeId === SOURCE_TYPE.JUNIPER_MIST && Boolean(timeIntervalInputs);

/** Checks if any data is currently being loaded */
const isLoadingData = (queries: {
  sourceTypeBasicQuery: { isLoading?: boolean };
  sourceTypeSchemaQuery: { isLoading?: boolean };
  sourceLogTypeQuery: { isLoading: boolean };
  sourceDetailQuery: { isLoading?: boolean };
  limaDevicesQuery: { isLoading?: boolean };
}): boolean =>
  Boolean(
    queries.sourceTypeBasicQuery?.isLoading ||
      queries.sourceTypeSchemaQuery?.isLoading ||
      queries.sourceLogTypeQuery.isLoading ||
      queries.sourceDetailQuery?.isLoading ||
      queries.limaDevicesQuery?.isLoading
  );

type SourceCreateFormProps = {
  isCustomerCloudSourcePage?: boolean;
};

const SourceCreateForm = ({
  isCustomerCloudSourcePage
}: SourceCreateFormProps) => {
  const { mode: themeMode } = useColorScheme();
  const formStyles = useFormStyles();
  const sourceStyles = useSourceCreateStyles({ themeMode });
  const { register } = useFormContext();

  const [currentStep] = useAtom(wizardCurrentStepAtom);
  const [wizardSelectedSource] = useAtom(wizardSelectedSourceAtom);

  const {
    mode,
    sourceTypeBasicQuery,
    sourceTypeSchemaQuery,
    sourceLogTypeQuery,
    sourceDetailQuery,
    limaDevicesQuery,
    connectionInputSchema,
    selectedSourceTypeId,
    selectedIngestConfigTab,
    handleSourceTypeChange
  } = useCloudSourceForm(isCustomerCloudSourcePage);

  // Set default values on initial load on source edit mode
  useSourceDetailsForEdit();
  useCustomValidatons();

  // Get state and handlers from the refactored hook
  const {
    categories,
    regions,
    deviceAlertFields,
    categoryMasterList,
    handleCategoryChange,
    handleRegionChange,
    handleDeviceAlertFieldsChange,
    setSourceStatus,
    sourceStatus,
    selectedSourceDetails,
    isResettingSourceType,
    hasIngestMethodErrors,
    hasDataProviderError,
    handleIngestConfigDataChange
  } = useSourceFormState();

  /**
   * Initialize schema when entering Step 2 (Configure Source) in New mode.
   *
   * Business Rule: In the wizard flow, the form is reset on Step 1 mount.
   * When moving to Step 2, we must explicitly trigger schema loading for the
   * selected source type to ensure all dynamic fields are available.
   *
   * Why needed: Without this, the form would show no fields until the user
   * changes the source type dropdown, creating a confusing empty state.
   */
  useEffect(() => {
    const shouldInitializeSchema =
      mode === SourceDialogMode.New &&
      currentStep === WIZARD_STEP.CONFIGURE_SOURCE &&
      wizardSelectedSource &&
      selectedSourceTypeId !== wizardSelectedSource.source_type_id;

    if (shouldInitializeSchema) {
      // Trigger schema load and state setup for the selected source
      handleSourceTypeChange(wizardSelectedSource.source_type_id);
    }
  }, [
    mode,
    wizardSelectedSource,
    handleSourceTypeChange,
    currentStep,
    selectedSourceTypeId
  ]);

  useCategoryValidations();

  // Determine which queries are currently loading
  const isLoading = isLoadingData({
    sourceTypeBasicQuery,
    sourceTypeSchemaQuery,
    sourceLogTypeQuery,
    sourceDetailQuery,
    limaDevicesQuery
  });

  // Extract boolean flags for clearer JSX
  const showSiteNameFirst = shouldRenderSiteNameFirst(selectedSourceTypeId);
  const showIngestConfigTab = isIngestConfigTabActive(selectedIngestConfigTab);
  const showDataProviderTab = isDataProviderTabActive(selectedIngestConfigTab);
  const showTimeInterval = shouldShowTimeInterval(
    selectedSourceTypeId,
    connectionInputSchema?.sub_time_interval_inputs
  );
  const hasContainerIngest = Boolean(
    connectionInputSchema?.is_container_ingest_enabled
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <LoadingIndicator isLoading={isLoading} />

      <Grid
        className={formStyles.classes.sourceCreateFormGrid}
        container
        spacing={2}
      >
        {/* Core Identity Fields: Organization, Device, IP Address */}
        <OrgDeviceIPFields />

        {/* Source Type Selection Dropdown */}
        <SourceTypeSelector />

        {/*
          SubSourceTypeSelector Position Logic:
          - Microsoft Entra ID: Shows AFTER site_name field (below)
          - All other sources: Shows immediately after source type selector

          Business Rule: Entra ID requires site_name to be entered first
          because it uses the site name to configure the data provider
          before sub-source selection, while other sources can select
          the sub-source type independently.
        */}
        {showSiteNameFirst && <SubSourceTypeSelector />}

        {/* Site Name Field - Conditional position for Entra ID */}
        <SiteNameField
          name="site_name"
          srcTypeId={selectedSourceTypeId}
          dataSourceName=""
          xs={12}
          sm={6}
          md={4}
        />

        {/* SubSourceTypeSelector for non-Entra ID sources */}
        {!showSiteNameFirst && <SubSourceTypeSelector />}

        {/* Ingest Method Tabs - Only shown for container-enabled sources */}
        {connectionInputSchema?.is_container_ingest_enabled && (
          <IngestDataTabs
            selectedTab={selectedIngestConfigTab}
            onTabChange={handleIngestConfigDataChange}
            hasIngestMethodErrors={hasIngestMethodErrors}
            hasDataProviderError={hasDataProviderError}
            className={sourceStyles.classes.ingestDataTab}
          />
        )}

        {/*
          Ingest Method Configuration Tab
          Contains the primary source configuration fields that define how
          data is ingested from the source system. Enabled by default when
          not using container-based ingestion.
        */}
        <Collapse
          className={
            hasContainerIngest
              ? formStyles.classes.collapsableTabGroup
              : undefined
          }
          sx={{ width: '100%' }}
          in={showIngestConfigTab}
          unmountOnExit={false}
        >
          <Grid
            container
            spacing={2}
            key={`source-type-${selectedSourceTypeId}`}
          >
            {/* Schema-driven dynamic form fields */}
            <DynamicFieldsList
              sourceInputs={connectionInputSchema?.source_inputs}
              selectedSourceTypeId={selectedSourceTypeId}
              mode={mode}
              isResettingSourceType={isResettingSourceType}
            />

            {/* Region selection for cloud providers */}
            <RegionSelector
              connectionInputSchema={connectionInputSchema}
              regions={regions}
              onRegionChange={handleRegionChange}
              isResettingSourceType={isResettingSourceType}
              className={sourceStyles.classes.multiSelectForm}
            />

            {/* Log category selection */}
            <CategorySelector
              connectionInputSchema={connectionInputSchema}
              categoryMasterList={categoryMasterList}
              categories={categories}
              onCategoryChange={handleCategoryChange}
              isResettingSourceType={isResettingSourceType}
              className={sourceStyles.classes.multiSelectForm}
            />

            {/* Multi-level category hierarchy (e.g., nested log types) */}
            <MultiLevelCategorySelector
              selectedSourceTypeId={selectedSourceTypeId}
              connectionInputSchema={connectionInputSchema}
              isResettingSourceType={isResettingSourceType}
            />

            {/*
              Time Interval Configuration - Juniper Mist Specific
              Business Rule: Juniper Mist supports separate polling intervals
              for Tickets API and Guest Auth API to optimize API rate limits.
            */}
            {showTimeInterval && (
              <SubTimeInterval
                subTimeIntervalSchema={
                  connectionInputSchema.sub_time_interval_inputs!
                }
              />
            )}

            {/* Alert device configuration - Medigate specific */}
            <AlertDeviceFieldsSelector
              connectionInputSchema={connectionInputSchema}
              deviceAlertFields={deviceAlertFields}
              onDeviceAlertFieldsChange={handleDeviceAlertFieldsChange}
              isResettingSourceType={isResettingSourceType}
            />

            {/* Device vulnerability polling - Medigate specific */}
            {!isResettingSourceType &&
              selectedSourceTypeId === SOURCE_TYPE.MEDIGATE &&
              connectionInputSchema?.fetch_device_vulnerabilities && (
                <FetchDeviceVulnerability
                  vulSchema={connectionInputSchema.fetch_device_vulnerabilities}
                />
              )}

            {/* Custom object ingestion - Salesforce specific */}
            {!isResettingSourceType &&
              selectedSourceTypeId === SOURCE_TYPE.SALESFORCE &&
              connectionInputSchema?.fetch_custom_object_events && (
                <FetchCustomObjects
                  fetchObjSchema={
                    connectionInputSchema.fetch_custom_object_events
                  }
                />
              )}

            {/* Acronis-specific form fields (Category and Topics) */}
            {!isResettingSourceType &&
              selectedSourceTypeId === SOURCE_TYPE.ACRONIS && (
                <AcronisTopics
                  mode={mode}
                  isResettingSourceType={isResettingSourceType}
                />
              )}

            {/*
              Authentication Method Selection
              Allows choosing between Client Secret or Certificate-based auth
              for sources that support both (e.g., Microsoft O365, Azure AD).
            */}
            <AuthModeSelector />

            {/* Certificate download/regeneration for existing sources */}
            <CertificateControls />

            {/* Client Secret authentication fields */}
            <ClientSecretFields />

            {/* Existing certificate upload fields */}
            <ExistingCertificateFields />

            {/* New certificate generation fields */}
            <NewCertificateFields />

            {/* Active/Inactive toggle for the source */}
            <ActiveToggle
              mode={mode}
              sourceStatus={sourceStatus}
              onSourceStatusChange={setSourceStatus}
              selectedSourceDetails={selectedSourceDetails}
              xs={12}
              sm={12}
              md={12}
            />

            {/* Source-specific help text and documentation links */}
            <SourceInfoBlock sourceTypeId={selectedSourceTypeId} />
          </Grid>
        </Collapse>

        {/*
          Data Provider Tab
          Only shown for container-enabled sources (e.g., Microsoft sources).
          Allows configuring multiple data providers under a single source
          container to support multi-tenant scenarios.

          Business Rule: Container ingest enables grouping related sources
          (e.g., multiple O365 tenants) under one configuration.
        */}
        <Collapse
          className={
            hasContainerIngest
              ? formStyles.classes.collapsableTabGroup
              : undefined
          }
          sx={{ width: '100%' }}
          in={showDataProviderTab}
          unmountOnExit={false}
        >
          <Grid
            container
            spacing={2}
            key={`data-provider-${selectedSourceTypeId}`}
          >
            {connectionInputSchema?.is_container_ingest_enabled && (
              <Box sx={{ width: '100%' }}>
                <input
                  type="hidden"
                  {...register('data_provider_selection', {
                    required: 'Select at least one data provider'
                  })}
                />
                <DataProvider
                  parent_name="data_provider"
                  data_sources={connectionInputSchema?.data_sources || []}
                />
              </Box>
            )}
          </Grid>
        </Collapse>
      </Grid>
    </Box>
  );
};

export default SourceCreateForm;
